var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Common/QRCodeButton.less';
import '@/styles/InitiativeTracker/InitiativeTracker.less';
import '@/styles/Rooms/InitiativeTrackerRoom.less';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useApiInstance } from '@/api/index';
import { BASE_URL, isHttps } from '@/api/realApi/_constants';
import { Icon } from '@/assets/icons/LibrarySvg';
import CharacterDetailsSidebar from '@/components/Common/CharacterDetailsSidebar';
import DiceBar from '@/components/Common/DiceBar';
import QRCodeModal from '@/components/Common/QRCodeModal';
import CombatLogs from '@/components/InitiativeTracker/CombatLogs';
import InitiativeCard from '@/components/InitiativeTracker/InitiativeCard';
import InitiativeTrackerCardInfo from '@/components/InitiativeTracker/InitiativeTrackerCardInfo';
import LongRestButton from '@/components/InitiativeTracker/LongRestButton';
import ControlsContainer from '@/components/UI/ControlsContainer';
import Loader from '@/components/UI/Loader';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { useInitiativeTracker } from '@/context/InitiativeTrackerContext';
import { useCardsMemo } from '@/hooks/useCardsQueue';
import { useCenterActiveCard } from '@/hooks/useCenterActiveCard';
import useFullscreen from '@/hooks/useFullScreen';
import { sortCards } from '@/utils/sort';
import InitiativeTrackerHeader from '../../components/InitiativeTracker/InitiativeTrackerHeader';
const MemoInitiativeCard = memo(InitiativeCard);
const ws_schema = isHttps ? 'wss:' : 'ws:';
const wsUrl = `${ws_schema}//${BASE_URL}`;
const InitiativeTrackerRoom = ({ roomCode }) => {
    const { cards, setCards, selectedCardsIds, setSelectedCardsIds, activeCombatMap, setActiveCombatMap, setIsNextTurnLoading, loading, setLoading, isOwnerUser, setIsOwnerUser, setConnected, room, setRoom, } = useInitiativeTracker();
    const sortedCards = useCardsMemo(cards);
    const [showAddForm, setShowAddForm] = useState(false);
    // Sidebar state for editing/creating character inside Room
    const [sidebarCharacter, setSidebarCharacter] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showQRCodeModal, setShowQRCodeModal] = useState(false);
    const navigate = useNavigate();
    const { user, loading: isAuthLoading, userSettings, setUserSettings } = useAuth();
    const { useMockApi } = useApiContext();
    const cardsRowRef = useRef(null);
    const initiativeTrackerContainerRef = useRef(null);
    // useFullscreen hook
    const { isFullScreen, toggleFullScreen } = useFullscreen({
        targetRef: initiativeTrackerContainerRef,
        onEnter: () => {
            var _a;
            (_a = initiativeTrackerContainerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('full-screen');
        },
        onExit: () => {
            var _a;
            (_a = initiativeTrackerContainerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove('full-screen');
        },
    });
    const isHeaderVisible = isOwnerUser || !isFullScreen;
    const openCharacterSidebar = (char) => {
        console.log('[InitiativeTrackerRoom] openCharacterSidebar', char);
        setSidebarCharacter(char);
        setIsSidebarOpen(true);
    };
    const closeCharacterSidebar = () => {
        setIsSidebarOpen(false);
        setSidebarCharacter(null);
    };
    const api = useApiInstance();
    const roomApi = api.roomApiInstance;
    const combatTrackerApiInstance = api.combatTrackerApiInstance;
    const [roomLoading, setRoomLoading] = useState(true);
    const ws = useRef(null);
    const controlsContainerRef = useRef(null);
    // Проверяем, существует ли комната через API
    useEffect(() => {
        setRoomLoading(true);
        setRoom(null);
        roomApi
            .getRoomByCode(roomCode)
            .then((room) => setRoom(room))
            .catch((err) => {
            console.error('[Room] Error fetching room:', err);
            setRoom(null);
        })
            .finally(() => setRoomLoading(false));
    }, [roomApi, roomCode, setRoom]);
    // начальная загрузка карты боя && карточек
    useEffect(() => {
        if (isAuthLoading || roomLoading)
            return;
        if (!room) {
            console.error('[InitiativeTracker] No room found');
            return;
        }
        if (useMockApi) {
            navigate('/initiative-tracker', { replace: true });
            return;
        }
        // console.log('room: ', room);
        combatTrackerApiInstance.combat_map
            .getCombatMapByRoomId(room.id)
            .then((data) => {
            var _a;
            // console.log('[InitiativeTracker] Active combat maps:', data);
            const combatMap = data;
            setActiveCombatMap(combatMap);
            setCards(sortCards((_a = combatMap.characters) !== null && _a !== void 0 ? _a : []));
            setLoading(false);
        })
            .catch((err) => {
            console.error('[InitiativeTracker] Error fetching active combat map:', err);
            setActiveCombatMap(null);
            // найти другие карты боя пользователя без комнаты
            combatTrackerApiInstance.combat_map
                .list({ only_unbinded_maps: true, created_by_id: user === null || user === void 0 ? void 0 : user.id })
                .then((data) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                // console.log('[InitiativeTracker] Active combat maps:', data);
                if (data.length > 0) {
                    setActiveCombatMap(data[0]);
                    yield combatTrackerApiInstance.combat_map.update(data[0].id, { roomId: room.id });
                    setCards(sortCards((_a = data[0].characters) !== null && _a !== void 0 ? _a : []));
                    setLoading(false);
                }
                else {
                    // создать новую карту боя
                    combatTrackerApiInstance.combat_map
                        .create({ name: 'New Combat Map', roomId: room.id })
                        .then((newCombatMap) => __awaiter(void 0, void 0, void 0, function* () {
                        console.log('[InitiativeTracker] Created new combat map:', newCombatMap);
                        setActiveCombatMap(newCombatMap);
                        yield combatTrackerApiInstance.combat_map.update(newCombatMap.id, {
                            roomId: room.id,
                        });
                        setLoading(false);
                    }))
                        .catch((err) => {
                        console.error('[InitiativeTracker] Error creating combat map:', err);
                    });
                }
            }));
        });
    }, [
        user,
        isAuthLoading,
        useMockApi,
        navigate,
        room,
        roomLoading,
        combatTrackerApiInstance.combat_map,
        setActiveCombatMap,
        setCards,
        setLoading,
    ]);
    // Устанавливаем владельца комнаты
    useEffect(() => {
        if (isAuthLoading)
            return;
        if (activeCombatMap && user) {
            setIsOwnerUser(user.id === activeCombatMap.createdBy);
        }
    }, [user, activeCombatMap, isAuthLoading, setIsOwnerUser]);
    // Подключение к WebSocket, подписка на события комнаты && обработка сообщений
    useEffect(() => {
        if (isAuthLoading)
            return;
        if (!room)
            return;
        const MAX_RECONNECT = 6;
        const BASE_DELAY = 700; // ms
        const reconnectTimerRef = { current: null };
        const reconnectAttemptsRef = { current: 0 };
        let mounted = true;
        const connect = () => {
            if (!mounted)
                return;
            ws.current = new WebSocket(`${wsUrl}/ws/room/${roomCode}/`);
            ws.current.onopen = () => {
                var _a;
                reconnectAttemptsRef.current = 0;
                setConnected(true);
                setLoading(false);
                console.log('[Room] WebSocket opened');
                // Подписка на изменения Character для этой комнаты
                const subscribeMsg = {
                    action: 'subscribe',
                    request_id: new Date().getTime(),
                };
                (_a = ws.current) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(subscribeMsg));
            };
            // Обработка входящих сообщений
            ws.current.onmessage = (event) => {
                const message = JSON.parse(event.data);
                // console.log('[Room] <<== Received message:', message);
                if (message.errors && message.errors.length > 0) {
                    console.error('[Room] Error from server:', message.errors);
                    return;
                }
                if (message.action === 'list') {
                    setCards(sortCards(message.data || []));
                    // console.log('[Room] Set cards (list):', message.data);
                }
                else if (message.action === 'create') {
                    setCards((prev) => {
                        const updated = [...prev, message.data];
                        // console.log('[Room] Set cards (create):', updated);
                        return sortCards(updated);
                    });
                }
                else if (message.action === 'update' || message.action === 'patch') {
                    const objectId = message.data ? message.data.pk || message.data.id : null;
                    if (!objectId) {
                        console.error('[Room] No objectId in update/patch message:', message);
                        return;
                    }
                    setCards((prev) => {
                        const deactivateOtherCards = message.data.isActive === true;
                        const defaults = { divideDamage: false, multiplyDamage: false };
                        let updated;
                        if (deactivateOtherCards) {
                            updated = prev.map((card) => card.id === objectId
                                ? Object.assign(Object.assign(Object.assign({}, card), message.data), defaults) : Object.assign(Object.assign(Object.assign({}, card), defaults), { isActive: false }));
                        }
                        else {
                            updated = prev.map((card) => card.id === objectId
                                ? Object.assign(Object.assign(Object.assign({}, card), message.data), defaults) : Object.assign(Object.assign({}, card), defaults));
                        }
                        // console.log('[Room] Set cards (update/patch):', updated);
                        return sortCards(updated);
                    });
                    // setIsNextTurnLoading(false);
                }
                else if (message.action === 'delete' || message.action === 'remove_character_from_map') {
                    const objectIds = message.data ? message.data.removedIds : null;
                    if (!objectIds) {
                        console.error('[Room] No objectIds in delete/remove_character_from_map message:', message);
                        return;
                    }
                    setCards((prev) => {
                        const updated = prev.filter((c) => !objectIds.includes(c.id));
                        // console.log('[Room] Set cards (delete):', updated, 'objectIds:', objectIds);
                        return sortCards(updated);
                    });
                }
                else if (message.action === 'add_character_to_map') {
                    const objectIds = message.data ? message.data.addedIds : null;
                    if (!objectIds) {
                        console.error('[Room] No objectIds in update/patch message:', message);
                        return;
                    }
                    setCards((prev) => {
                        const newCharacters = message.data.addedCharacters.map((char) => (Object.assign(Object.assign({}, char), { initiative: 0 })));
                        const updated = [...prev, ...newCharacters];
                        return sortCards(updated);
                    });
                }
                else if (message.action === 'update_combat_data') {
                    // Обновление данных карты боя
                    const objectId = message.data ? message.data.pk || message.data.id : null;
                    if (!objectId) {
                        console.error('[Room] No objectId in update_combat_data message:', message);
                        return;
                    }
                    setCards((prev) => {
                        const characterExists = prev.find((c) => c.id == objectId);
                        const deactivateOtherCards = message.data.isActive === true;
                        // console.log('[Room] Character exists:', characterExists, 'objectId:', objectId);
                        let updated;
                        if (characterExists) {
                            if (deactivateOtherCards) {
                                updated = prev.map((card) => card.id === objectId
                                    ? Object.assign(Object.assign({}, card), message.data) : Object.assign(Object.assign({}, card), { isActive: false }));
                            }
                            else {
                                updated = prev.map((card) => card.id === objectId ? Object.assign(Object.assign({}, card), message.data) : card);
                            }
                        }
                        else {
                            if (deactivateOtherCards) {
                                updated = prev.map((card) => (Object.assign(Object.assign({}, card), { isActive: false })));
                            }
                            else {
                                updated = [...prev, Object.assign(Object.assign({}, message.data), { id: objectId })];
                            }
                        }
                        // console.log('[Room] updated:', updated);
                        setSelectedCardsIds([]);
                        return sortCards(updated);
                    });
                    setIsNextTurnLoading(false);
                }
                else if (message.action === 'sync_selected_characters') {
                    // Обновление выделенных персонажей
                    const selectedIds = message.data.selectedCharacters;
                    setSelectedCardsIds([...selectedIds]);
                    // console.log('[Room] Set selected cards:', selectedIds);
                }
                else if (message.action === 'sync_selected_combat_map') {
                    // Обновление выбранной карты боя
                    setLoading(false);
                    const selectedCombatMapId = message.data.selectedCombatMapId;
                    combatTrackerApiInstance.combat_map
                        .get(selectedCombatMapId)
                        .then((data) => __awaiter(void 0, void 0, void 0, function* () {
                        var _a;
                        // console.log('[InitiativeTracker] Active combat map:', data);
                        setActiveCombatMap(data);
                        setCards(sortCards((_a = data.characters) !== null && _a !== void 0 ? _a : []));
                        setLoading(false);
                    }));
                    // console.log('[Room] sync_selected_combat_map [message]:', message);
                    // console.log('[Room] sync_selected_combat_map [message.data]:', message.data);
                    // console.log('[Room] sync_selected_combat_map [selectedCombatMapId]:', selectedCombatMapId);
                }
                else if (message.action === 'set_user_settings') {
                    // Обновление настроек пользователя
                    const updatedUserSettings = message.data.settings;
                    if (!updatedUserSettings)
                        return;
                    setUserSettings(updatedUserSettings);
                    // console.log('[Room] User settings updated:', updatedUserSettings);
                }
            };
            // Обработка ошибок WebSocket
            ws.current.onerror = (e) => {
                console.error('[Room] WebSocket error:', e);
            };
            ws.current.onclose = (ev) => {
                setConnected(false);
                console.log('[Room] WebSocket closed', ev.code, ev.reason, ev);
                if (!mounted)
                    return;
                // попытка переподключения с экспоненциальной задержкой
                if (reconnectAttemptsRef.current < MAX_RECONNECT) {
                    const delay = BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current);
                    reconnectAttemptsRef.current += 1;
                    reconnectTimerRef.current = window.setTimeout(() => {
                        connect();
                    }, delay);
                    console.info(`[Room] Reconnect scheduled in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
                }
                else {
                    console.warn('[Room] Max reconnect attempts reached, will not reconnect automatically.');
                }
            };
        };
        connect();
        return () => {
            var _a;
            // Очистка WebSocket при размонтировании компонента
            mounted = false;
            if (reconnectTimerRef.current !== null) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            (_a = ws.current) === null || _a === void 0 ? void 0 : _a.close();
        };
    }, [
        roomCode,
        room,
        isAuthLoading,
        setConnected,
        setLoading,
        setCards,
        setIsNextTurnLoading,
        setSelectedCardsIds,
        combatTrackerApiInstance.combat_map,
        setActiveCombatMap,
        setUserSettings,
    ]);
    // --- CRUD через сокеты ---
    const handleAdd = (character) => __awaiter(void 0, void 0, void 0, function* () {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            const msg = {
                action: 'create',
                data: Object.assign(Object.assign({}, character), { combatMapId: activeCombatMap.id }),
            };
            ws.current.send(JSON.stringify(msg));
            // console.log('[Room] ==>> Sent create:', msg);
        }
    });
    const handleDelete = (character) => __awaiter(void 0, void 0, void 0, function* () {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            // const msg: RoomActionMessage = { action: 'delete', pk: id };
            const msg = {
                action: 'remove_character_from_map',
                data: { combat_map_id: activeCombatMap.id, character_id: character.id },
            };
            ws.current.send(JSON.stringify(msg));
            // console.log('[Room] ==>> Sent delete:', msg);
        }
    });
    const handleUpdate = (card) => __awaiter(void 0, void 0, void 0, function* () {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            const msg = {
                action: 'patch',
                request_id: new Date().getTime(),
                pk: card.id,
                data: Object.assign(Object.assign({}, card), { combatMapId: activeCombatMap.id }),
            };
            ws.current.send(JSON.stringify(msg));
            // console.log('[Room] ==>> Sent update:', msg);
        }
    });
    const handleAddCardToMap = (character) => __awaiter(void 0, void 0, void 0, function* () {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            const msg = {
                action: 'add_character_to_map',
                data: { combat_map_id: activeCombatMap.id, character_id: character.id },
            };
            ws.current.send(JSON.stringify(msg));
        }
    });
    const handleLongRestUpdated = useCallback((updatedCharacters) => {
        setCards((prev) => sortCards(prev.map((c) => {
            const patch = updatedCharacters.find((u) => u.id === c.id);
            return patch ? Object.assign(Object.assign({}, c), patch) : c;
        })));
    }, [setCards]);
    // --- LOCAL методы ---
    const toggleAddCardFormVisibility = () => setShowAddForm((v) => !v);
    const handleChooseCombatMap = (map) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield combatTrackerApiInstance.combat_map.update(map.id, { roomId: room.id });
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                const msg = {
                    action: 'translate_selected_combat_map',
                    data: { combat_map_id: map.id, room_code: roomCode },
                };
                ws.current.send(JSON.stringify(msg));
                console.log('[Room] ==>> Sent translate_selected_combat_map:', msg);
            }
            setActiveCombatMap(map);
            setSelectedCardsIds([]);
            setCards(sortCards(map.characters || []));
        }
        catch (e) {
            console.error('[InitiativeTrackerRoom] Error choosing combat map:', e);
        }
    });
    // --- CARDS ---
    // debounced helper: при серии быстрых вызовов выполнится только последний
    const debouncedSetActiveCard = useDebouncedCallback((cardId, activeCombatMapId) => {
        const cardData = { id: cardId, isActive: true, combatMapId: activeCombatMapId };
        handleUpdate(cardData);
        // снять индикатор загрузки по завершении отправки
        setIsNextTurnLoading(false);
    }, 700);
    const handleNext = () => {
        if (cards.length > 1) {
            setCards((prev) => {
                const currentIdx = prev.findIndex((card) => card.isActive);
                if (currentIdx === -1) {
                    const charactersAlive = prev.filter((card) => card.health > 0);
                    if (charactersAlive.length > 0) {
                        debouncedSetActiveCard(charactersAlive[0].id, activeCombatMap.id);
                    }
                    setIsNextTurnLoading(false);
                    return sortCards(prev);
                }
                let nextIdx = (currentIdx + 1) % prev.length;
                while (prev[nextIdx].health <= 0) {
                    nextIdx = (nextIdx + 1) % prev.length;
                    if (nextIdx === currentIdx) {
                        setIsNextTurnLoading(false);
                        return prev;
                    }
                }
                const nextCard = prev[nextIdx];
                debouncedSetActiveCard(nextCard.id, activeCombatMap.id);
                return prev.map((card, idx) => (Object.assign(Object.assign({}, card), { isActive: idx === nextIdx, divideDamage: false })));
            });
            setSelectedCardsIds([]);
        }
    };
    const handleSelect = useCallback((id, event) => {
        if (event) {
            const target = event.target;
            if (target.closest('button'))
                return;
        }
        setSelectedCardsIds((prev) => {
            const selected = prev.includes(id)
                ? prev.filter((selectedId) => selectedId !== id)
                : [...prev, id];
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                const msg = {
                    action: 'translate_selected_characters',
                    data: { character_ids: selected, room_code: roomCode },
                };
                ws.current.send(JSON.stringify(msg));
                console.log('[Room] ==>> Sent translate_selected_characters:', msg);
            }
            return selected;
        });
    }, [roomCode, setSelectedCardsIds]);
    useEffect(() => {
        // Отчищаем комнату при размонтировании компонента
        return () => {
            setRoom(null);
        };
    }, [setRoom]);
    const onSetDivideDamage = (card, value) => {
        setCards((prev) => prev.map((c) => (Object.assign(Object.assign({}, c), { divideDamage: c.id === card.id ? value : c.divideDamage }))));
    };
    const onSetMultiplyDamage = (card, value) => {
        setCards((prev) => prev.map((c) => (Object.assign(Object.assign({}, c), { multiplyDamage: c.id === card.id ? value : c.multiplyDamage }))));
    };
    // Центрирование скролла
    useCenterActiveCard(cards, cardsRowRef);
    if (loading)
        return _jsx(Loader, {});
    return (_jsxs("div", { className: "initiative-tracker-container", children: [_jsx(ControlsContainer, { ref: controlsContainerRef, position: "bottom-left", direction: "column" }), _jsxs("div", { className: "initiative-tracker-wrapper", children: [_jsxs("div", { className: "initiative-tracker", ref: initiativeTrackerContainerRef, children: [_jsx(InitiativeTrackerHeader, { user: user, handleNext: handleNext, toggleAddCardFormVisibility: toggleAddCardFormVisibility, showAddForm: showAddForm, handleAddCard: handleAdd, handleUpdateCard: handleUpdate, handleChooseCombatMap: handleChooseCombatMap, handleAddCardToMap: handleAddCardToMap, styles: !isHeaderVisible ? { display: 'none' } : undefined, pageButtonsContainer: controlsContainerRef.current }), !isOwnerUser && (_jsx("div", { className: `full-screen-button-container ${isFullScreen ? 'is-full-screen' : ''}`, children: _jsx("button", { className: "full-screen-button", onClick: toggleFullScreen, children: isFullScreen ? _jsx(Icon, { name: "exit_full_screen_1" }) : _jsx(Icon, { name: "full_screen_1" }) }) })), _jsx("div", { className: "cards-row-wrapper", children: _jsx("div", { className: "cards-row", ref: cardsRowRef, children: sortedCards.map((card) => (_jsx(MemoInitiativeCard, { card: card, onDelete: handleDelete, onUpdate: handleUpdate, onOpenDetails: openCharacterSidebar, onSelect: handleSelect, isSelected: selectedCardsIds.includes(card.id), isOwnerUser: isOwnerUser, userSettings: userSettings, onSetDivideDamage: onSetDivideDamage, onSetMultiplyDamage: onSetMultiplyDamage }, card.id))) }) })] }), activeCombatMap && (_jsx(CombatLogs, { combatMap: activeCombatMap, pageButtonsContainer: controlsContainerRef.current, showToggleButton: isOwnerUser }))] }), isOwnerUser && (_jsx(InitiativeTrackerCardInfo, { character: cards.find((card) => card.isActive), onUpdate: handleUpdate, pageButtonsContainer: controlsContainerRef.current })), isOwnerUser && (_jsx(LongRestButton, { pageButtonsContainer: controlsContainerRef.current, show: true, onUpdated: handleLongRestUpdated })), !isFullScreen && _jsx(DiceBar, { pageButtonsContainer: controlsContainerRef.current }), isOwnerUser &&
                controlsContainerRef.current &&
                createPortal(_jsx("button", { type: "button", className: "qr-code-toggle-button", onClick: () => setShowQRCodeModal(true), title: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C QR-\u043A\u043E\u0434 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u044B", style: { order: 0 }, children: _jsx(Icon, { name: "qr_1" }) }), controlsContainerRef.current), isSidebarOpen &&
                sidebarCharacter &&
                createPortal(_jsx(CharacterDetailsSidebar, { character: sidebarCharacter, isOpen: isSidebarOpen, onClose: closeCharacterSidebar, onSave: handleUpdate, onDelete: handleDelete, combatMap: activeCombatMap }), document.body), _jsx(QRCodeModal, { isOpen: showQRCodeModal, onClose: () => setShowQRCodeModal(false) })] }));
};
export default InitiativeTrackerRoom;
